/**
 * Modal Component
 * Phase 5: Code Quality - Base modal component to eliminate duplication
 *
 * BEFORE: Modal backdrop logic duplicated in 3+ components
 * AFTER: Single reusable Modal component with consistent behavior
 */

import React, { useEffect, useCallback } from 'react';
import { useTheme } from '../../hooks/useTheme';
import '../../styles/shared/Modal.css';
import { CloseIcon } from '@atlas-forms/ui-components-react';

export interface ModalProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;

  /**
   * Callback when modal should close
   */
  onClose: () => void;

  /**
   * Modal title (can be string or custom React element)
   */
  title?: React.ReactNode;

  /**
   * Modal content
   */
  children: React.ReactNode;

  /**
   * Modal size
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large' | 'fullscreen';

  /**
   * Whether to show close button
   * @default true
   */
  showCloseButton?: boolean;

  /**
   * Whether clicking backdrop closes modal
   * @default true
   */
  closeOnBackdropClick?: boolean;

  /**
   * Whether ESC key closes modal
   * @default true
   */
  closeOnEscape?: boolean;

  /**
   * Custom className for modal container
   */
  className?: string;

  /**
   * Custom className for modal content
   */
  contentClassName?: string;

  /**
   * Footer content
   */
  footer?: React.ReactNode;

  /**
   * ARIA label for accessibility
   */
  ariaLabel?: string;
}

/**
 * Base Modal Component
 *
 * Provides consistent modal behavior across the application:
 * - ESC key handling
 * - Backdrop click handling
 * - Focus management
 * - Dark mode support
 * - Accessibility (ARIA)
 * - Animations
 *
 * @example
 * ```tsx
 * <Modal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Confirm Action"
 *   size="small"
 * >
 *   <p>Are you sure?</p>
 * </Modal>
 * ```
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'medium',
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  className = '',
  contentClassName = '',
  footer,
  ariaLabel,
}) => {
  const { theme } = useTheme();

  // Handle ESC key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (closeOnBackdropClick && e.target === e.currentTarget) {
        onClose();
      }
    },
    [closeOnBackdropClick, onClose]
  );

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={`af-modal af-modal--${size} ${className}`}
      data-theme={theme}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal={true}
      aria-label={ariaLabel || (typeof title === 'string' ? title : undefined) || 'Dialog'}
    >
      <div className={`af-modal__container ${contentClassName}`}>
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="af-modal__header">
            {title && (
              <h2 className="af-modal__title" id="modal-title">
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                className="af-modal__close"
                onClick={onClose}
                aria-label="Close dialog"
                title="Close (ESC)"
                type="button"
              >
                <CloseIcon width={16} height={16} />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="af-modal__content">{children}</div>

        {/* Footer */}
        {footer && <div className="af-modal__footer">{footer}</div>}
      </div>
    </div>
  );
};

export default Modal;
